import type { KeyboardEvent, ReactNode } from 'react';

import styles from './ModulePage.module.css';

export interface PageBreadcrumbProps {
  current: string;
  items: readonly string[];
}

export interface PageContentCardProps {
  children: ReactNode;
  className?: string;
}

export interface PageTab {
  disabled?: boolean;
  id: string;
  label: string;
  panel?: ReactNode;
}

export interface PageTabsProps {
  ariaLabel: string;
  onValueChange: (value: string) => void;
  panelClassName?: string;
  tabs: readonly PageTab[];
  value: string;
}

function tabDomId(id: string): string {
  return `${id}-tab`;
}

function panelDomId(id: string): string {
  return `${id}-panel`;
}

export function PageBreadcrumb({ current, items }: PageBreadcrumbProps) {
  return (
    <nav aria-label="当前位置" className={styles.breadcrumb}>
      <span>当前位置：</span>
      {items.map((item) => <span key={item}>{item}<span aria-hidden="true"> / </span></span>)}
      <strong>{current}</strong>
    </nav>
  );
}

export function PageContentCard({ children, className }: PageContentCardProps) {
  return <section className={`${styles.contentCard}${className ? ` ${className}` : ''}`}>{children}</section>;
}

export function PageTabs({ ariaLabel, onValueChange, panelClassName, tabs, value }: PageTabsProps) {
  const activeTab = tabs.find((tab) => tab.id === value);
  const enabledTabs = tabs.filter((tab) => !tab.disabled);

  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, tab: PageTab) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onValueChange(tab.id);
      return;
    }

    const currentIndex = enabledTabs.findIndex((candidate) => candidate.id === tab.id);
    if (currentIndex === -1 || enabledTabs.length === 0) return;

    const nextIndex = event.key === 'ArrowRight'
      ? (currentIndex + 1) % enabledTabs.length
      : event.key === 'ArrowLeft'
        ? (currentIndex - 1 + enabledTabs.length) % enabledTabs.length
        : event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? enabledTabs.length - 1
            : null;

    if (nextIndex === null) return;
    event.preventDefault();
    document.getElementById(tabDomId(enabledTabs[nextIndex].id))?.focus();
  }

  return (
    <div className={styles.tabsRoot}>
      <div aria-label={ariaLabel} className={styles.tabList} role="tablist">
        {tabs.map((tab) => {
          const selected = tab.id === value;
          return (
            <button
              aria-controls={panelDomId(tab.id)}
              aria-selected={selected}
              className={`${styles.tab}${selected ? ` ${styles.activeTab}` : ''}`}
              disabled={tab.disabled}
              id={tabDomId(tab.id)}
              key={tab.id}
              role="tab"
              tabIndex={selected ? 0 : -1}
              type="button"
              onClick={() => onValueChange(tab.id)}
              onKeyDown={(event) => onTabKeyDown(event, tab)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {activeTab ? (
        <div
          aria-labelledby={tabDomId(activeTab.id)}
          className={`${styles.tabPanel}${panelClassName ? ` ${panelClassName}` : ''}`}
          id={panelDomId(activeTab.id)}
          role="tabpanel"
        >
          {activeTab.panel}
        </div>
      ) : null}
    </div>
  );
}
